import { QueueIsoDisplay } from "../component/map/QueueIsoColumns";
import { QueueSQLData, Filters, QueueFilterOptions } from "../types/queueTypes";
import { FilterOption } from "../component/filters/QueueInsightsGlobalFilter";
import * as turf from '@turf/turf';

// Helper function to strip non-alphanumeric characters from string
function stripNonAlphaNumeric(str: string): string {
    return str.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
}

// Utility function to extract values from FilterOption arrays
export function extractOptionValues(options: FilterOption[]): string[] {
    return options.map((option: FilterOption) => option.value);
}

// Utility function to create initial filter selections (all options selected)
export function createInitialFilterSelections(filterOptions: Filters): {
    generationTypes: string[];
    stateTypes: string[];
    countyTypes: string[];
    clusterTypes: string[];
} {
    return {
        generationTypes: extractOptionValues(
            filterOptions.generationTypeOptions
        ),
        stateTypes: extractOptionValues(filterOptions.stateTypeOptions),
        countyTypes: extractOptionValues(filterOptions.countyTypeOptions),
        clusterTypes: extractOptionValues(filterOptions.clusterTypeOptions),
    };
}

//Checks if a QueueItem is Valid -> Active
export function isValidQueueItem(
    item: QueueSQLData,
    isoFilter?: string | null
): boolean {
    // Allow any fuel type that is not empty or null
    const isValidFuelType = Boolean(item.Fuel_Type && item.Fuel_Type.trim() !== "");

    // If ISO filter is provided, check if item matches the filter
    if (isoFilter && isValidFuelType) {
        return (
            stripNonAlphaNumeric(item.ISO) === stripNonAlphaNumeric(isoFilter)
        );
    }

    // If no ISO filter is provided, return true if item is valid fuel type
    return isValidFuelType;
}

export function formatQueueIsoData(
    sqlData: QueueSQLData[],
    isoFilter?: string | null
): QueueIsoDisplay[] {
    if (!sqlData || sqlData.length === 0) return [];

    // Outer Map: Zone -> Zone Data, Inner Map: Zone Fuel Type -> Zone Fuel Type Data
    const zoneMap = new Map<
        string,
        {
            mw: number;
            projects: number;
            fuelTypes: Map<string, { mw: number; projects: number }>;
            iso: string;
            queueStatus: string;
        }
    >();

    sqlData.forEach((item) => {
        if (!isValidQueueItem(item, isoFilter)) {
            return;
        }

        const zone = item.Zone;
        const mw = item.Size_MW;
        const projects = 1;
        const fuelType = item.Fuel_Type;
        const iso = item.ISO;
        const queueStatus = "Active";

        // Add or update zone in the map
        if (!zoneMap.has(zone)) {
            zoneMap.set(zone, {
                mw: 0,
                projects: 0,
                fuelTypes: new Map(),
                iso: iso,
                queueStatus: queueStatus,
            });
        }

        const zoneData = zoneMap.get(zone)!;
        zoneData.mw += mw;
        zoneData.projects += projects;

        // Add or update fuel type within the zone
        if (!zoneData.fuelTypes.has(fuelType)) {
            zoneData.fuelTypes.set(fuelType, { mw: 0, projects: 0 });
        }

        const fuelTypeData = zoneData.fuelTypes.get(fuelType)!;
        fuelTypeData.mw += mw;
        fuelTypeData.projects += projects;
    });

    // Convert map to array of QueueIsoDisplay
    const result: QueueIsoDisplay[] = [];

    zoneMap.forEach((zoneData, zoneName) => {
        const zoneDisplay: QueueIsoDisplay = {
            name: zoneName,
            mw: zoneData.mw,
            projects: zoneData.projects,
            subRows: [],
            fuelType: "Mixed",
            iso: zoneData.iso,
            queueStatus: zoneData.queueStatus,
        };

        // Add fuel type subrows
        zoneData.fuelTypes.forEach((fuelTypeData, fuelTypeName) => {
            zoneDisplay.subRows!.push({
                name: fuelTypeName,
                mw: fuelTypeData.mw,
                projects: fuelTypeData.projects,
                fuelType: fuelTypeName,
                iso: zoneDisplay.iso,
                queueStatus: zoneDisplay.queueStatus,
            });
        });

        result.push(zoneDisplay);
    });

    return result;
}

export function getFilterOptions(data: {
    options: QueueSQLData[];
    loading: boolean;
}, isoFilter?: string | null): Filters | null {
    if (data.loading) return null;
    
    let filteredData = data.options;
    if (isoFilter) {
        filteredData = data.options.filter(entry => 
            stripNonAlphaNumeric(entry.ISO) === stripNonAlphaNumeric(isoFilter)
        );
    }
    
    const generationSet = new Set<string>();
    const clusterSet = new Set<string>();
    const stateSet = new Set<string>();
    const countySet = new Set<string>();
    
    filteredData.forEach((entry) => {
        generationSet.add(entry.Fuel_Type);
        clusterSet.add(entry.Queue_Cycle);
        stateSet.add(entry.State);
        countySet.add(entry.County);
    });
    
    return {
        generationTypeOptions: Array.from(generationSet)
            .sort()
            .map((value: string) => ({ value, label: value })),
        clusterTypeOptions: Array.from(clusterSet)
            .sort()
            .map((value: string) => ({ value, label: value })),
        stateTypeOptions: Array.from(stateSet)
            .sort()
            .map((value: string) => ({ value, label: value })),
        countyTypeOptions: Array.from(countySet)
            .sort()
            .map((value: string) => ({ value, label: value })),
    };
}

export function filterQueueData(
    queueData: QueueSQLData[],
    filters: QueueFilterOptions,
    isInitialized: boolean = false
): QueueSQLData[] {
    if (!queueData || queueData.length === 0) return [];

    return queueData.filter((item) => {
        if (
            filters.isoFilter &&
            stripNonAlphaNumeric(item.ISO) !==
                stripNonAlphaNumeric(filters.isoFilter)
        ) {
            return false;
        }

        // If not initialized yet, empty arrays mean data is loading.
        // If initialized, empty arrays mean "user deselected everything - show nothing"

        if (filters.generationTypes !== undefined) {
            if (isInitialized && filters.generationTypes.length === 0) {
                return false;
            }
            if (
                filters.generationTypes.length > 0 &&
                !filters.generationTypes.includes(item.Fuel_Type)
            ) {
                return false;
            }
        }

        if (filters.stateTypes !== undefined) {
            if (isInitialized && filters.stateTypes.length === 0) {
                return false;
            }
            if (
                filters.stateTypes.length > 0 &&
                !filters.stateTypes.includes(item.State)
            ) {
                return false;
            }
        }

        if (filters.countyTypes !== undefined) {
            if (isInitialized && filters.countyTypes.length === 0) {
                return false;
            }
            if (
                filters.countyTypes.length > 0 &&
                !filters.countyTypes.includes(item.County)
            ) {
                return false;
            }
        }

        if (filters.clusterTypes !== undefined) {
            if (isInitialized && filters.clusterTypes.length === 0) {
                return false;
            }
            if (
                filters.clusterTypes.length > 0 &&
                !filters.clusterTypes.includes(item.Queue_Cycle)
            ) {
                return false;
            }
        }

        // Geographic filtering using selected area
        if (filters.selectedArea && (filters.selectedArea.geometry || filters.selectedArea.bounds)) {
            if (item.Latitude === undefined || item.Longitude === undefined || 
                item.Latitude === null || item.Longitude === null) {
                return false;
            }

            const point = turf.point([item.Longitude, item.Latitude]);

            if (filters.selectedArea.mode === "Rectangle" && filters.selectedArea.bounds) {
                const [sw, ne] = filters.selectedArea.bounds;
                if (item.Longitude < sw[0] || item.Longitude > ne[0] ||
                    item.Latitude < sw[1] || item.Latitude > ne[1]) {
                    return false;
                }
            } else if (filters.selectedArea.geometry) {
                try {
                    if (filters.selectedArea.geometry.geometry.type === 'Polygon') {
                        if (!turf.booleanPointInPolygon(point, filters.selectedArea.geometry.geometry)) {
                            return false;
                        }
                    } else {
                        return false;
                    }
                } catch (error) {
                    console.warn('Error checking point in polygon:', error);
                    return false;
                }
            }
        }

        return true;
    });
}

// Creates a compound filter expression for the the mapbox queue layer
export function createQueueIsoLayerFilter(params: {
    isoFilter: string | null;
    generationTypes: string[];
    stateTypes: string[];
    countyTypes: string[];
    clusterTypes: string[];
}): any {
    const { isoFilter, generationTypes, stateTypes, countyTypes, clusterTypes } = params;
    const filters: any[] = [];

    //filter by active queue items
    const activeQueueFilter = ["==", ["get", "Queue Status"], "Active"];
    filters.push(activeQueueFilter);

    if (isoFilter) {
        filters.push(["==", ["get", "ISO"], isoFilter]);
    }

    if (generationTypes.length > 0) {
        filters.push([
            "any",
            ...generationTypes.map((g) => ["==", ["get", "Fuel Type"], g]),
        ]);
    } else {
        filters.push(["==", ["get", "Fuel Type"], "__NO_MATCH__"]);
    }

    if (stateTypes.length > 0) {
        filters.push([
            "any",
            ...stateTypes.map((s) => ["==", ["get", "State"], s]),
        ]);
    } else {
        filters.push(["==", ["get", "State"], "__NO_MATCH__"]);
    }

    if (countyTypes.length > 0) {
        filters.push([
            "any",
            ...countyTypes.map((c) => ["==", ["get", "County"], c]),
        ]);
    } else {
        filters.push(["==", ["get", "County"], "__NO_MATCH__"]);
    }

    if (clusterTypes.length > 0) {
        filters.push([
            "any",
            ...clusterTypes.map((cl) => ["==", ["get", "Queue Cycle"], cl]),
        ]);
    } else {
        filters.push(["==", ["get", "Queue Cycle"], "__NO_MATCH__"]);
    }

    if (filters.length === 0) return null;
    if (filters.length === 1) return filters[0];
    return ["all", ...filters];
}

// Normalize fuel types to the standardized 5 categories
export function normalizeFuelType(rawFuelType: string): string {
    if (!rawFuelType || typeof rawFuelType !== "string") {
        return "Other";
    }
    
    const fuelType = rawFuelType.toLowerCase().trim();
    
    if (fuelType.includes("solar")) {
        return "Solar";
    } else if (fuelType.includes("wind")) {
        return "Wind";
    } else if (
        fuelType.includes("battery") ||
        fuelType.includes("storage") ||
        fuelType.includes("battery storage")
    ) {
        return "Battery";
    } else if (
        fuelType.includes("conventional") ||
        fuelType.includes("thermal") ||
        fuelType.includes("gas") ||
        fuelType.includes("oil") ||
        fuelType.includes("coal")
    ) {
        return "Thermal";
    } else {
        return "Other";
    }
}

// Normalize queue data by standardizing fuel types
export function normalizeQueueData(queueData: QueueSQLData[]): QueueSQLData[] {
    return queueData.map(item => ({
        ...item,
        Fuel_Type: normalizeFuelType(item.Fuel_Type)
    }));
}
